import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Form, Button } from 'react-bootstrap';
import Swal from 'sweetalert2';
import Navigation from './Navigation';
import { FaUserFriends } from 'react-icons/fa';
import AdminFormLayout from './AdminFormLayout';

function MemberModuleForm() {
    const [outlets, setOutlets] = useState([]);
    const [members, setMembers] = useState([]);
    const [modules, setModules] = useState([]);
    const [selectedOutlet, setSelectedOutlet] = useState('');
    const [selectedMember, setSelectedMember] = useState('');
    const [selectedModules, setSelectedModules] = useState(new Set());
    const navigate = useNavigate();
    const { id } = useParams();

    useEffect(() => {
        const token = localStorage.getItem('sessionToken');
        if (!token) navigate('/loginadmin');
        else {
            fetchOutlets(token);
            fetchModules(token);
            if (id) fetchMemberModuleById(token);
        }
    }, [navigate, id]);

    const fetchOutlets = async (token) => {
        try {
            const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}outletlist`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify({ limit: 100, start: 0 })
            });
            const data = await response.json();
            if (data.error === "0" && data.results) {
                setOutlets(data.results);
            }
        } catch (err) {
            console.error('Failed to fetch outlets:', err);
        }
    };

    const fetchMembersByOutlet = async (token, outletId) => {
        try {
            const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}member/list`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify({ limit: 100, start: 0, outlet: parseInt(outletId, 10) })
            });
            const data = await response.json();
            
            if (data.error === 0 && data.results) {
                setMembers(data.results);
            }
        } catch (err) {
            console.error('Failed to fetch members:', err);
        }
    };

    const fetchModules = async (token) => {
        try {
            const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}modulelist`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify({ limit: 100, start: 0 })
            });
            const data = await response.json();
            if (data.error === "0" && data.results) {
                setModules(data.results);
            }
        } catch (err) {
            console.error('Failed to fetch modules:', err);
        }
    };

    const fetchMemberModuleById = async (token) => {
        try {
            const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}membermodule-details`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify({ id_member: parseInt(id) })
            });
            const data = await response.json();
            
            if (data.error === 0 && data.data) {
                setSelectedOutlet(data.data.id_outlet);
                setSelectedMember(data.data.id_member);
                setSelectedModules(new Set(data.data.modules));
                fetchMembersByOutlet(token, data.data.id_outlet);
            }
        } catch (err) {
            console.error('Failed to fetch member module:', err);
        }
    };

    const handleSave = async () => {
        const token = localStorage.getItem('sessionToken');
        try {
            const payload = {
                id_outlet: parseInt(selectedOutlet, 10),
                id_member: parseInt(selectedMember, 10),
                modules: Array.from(selectedModules)
            };

            const method = id ? 'PUT' : 'POST';
            const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}membermodule`, {
                method,
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify(payload)
            });

            const data = await response.json();
            console.log(JSON.stringify(payload));
            if (data.error === 0) {
                Swal.fire('Success', id ? 'Member module updated successfully' : 'Member module added successfully', 'success');
                navigate('/member-modules');
            } else {
                Swal.fire('Error', data.message || 'Failed to save member module', 'error');
            }
        } catch (err) {
            console.error('Failed to save member module:', err);
            Swal.fire('Error', 'Failed to save member module', 'error');
        }
    };

    const handleModuleChange = (moduleId) => {
        setSelectedModules(prev => {
            const newSelection = new Set(prev);
            if (newSelection.has(moduleId)) {
                newSelection.delete(moduleId);
            } else {
                newSelection.add(moduleId);
            }
            return newSelection;
        });
    };

    return (
        <div>
            <Navigation />
            <AdminFormLayout
                icon={FaUserFriends}
                eyebrow="Member access"
                title={id ? 'Update Member Module Assignment' : 'Create Member Module Assignment'}
                description="Tetapkan modul yang berlaku untuk member tertentu berdasarkan outlet agar layanan dan akses tercatat dengan baik."
                breadcrumbs={[
                    { label: 'Dashboard', to: '/dashboardadmin' },
                    { label: 'Member Module Assignment', to: '/member-modules' },
                    { label: id ? 'Update Assignment' : 'Create Assignment' },
                ]}
                tipsTitle="Assignment Notes"
                tips={[
                    'Pilih outlet terlebih dahulu untuk menampilkan daftar member yang relevan.',
                    'Tetapkan hanya modul yang sesuai dengan jenis layanan member.',
                    'Simpan perubahan setelah memastikan outlet, member, dan modul sudah benar.',
                ]}
            >
                <div className="field-stack">
                    <Form.Label>Outlet</Form.Label>
                    <Form.Select value={selectedOutlet} onChange={(e) => {
                        setSelectedOutlet(e.target.value);
                        const token = localStorage.getItem('sessionToken');
                        fetchMembersByOutlet(token, e.target.value);
                    }}>
                        <option value="">Select outlet</option>
                        {outlets.map(outlet => (
                            <option key={outlet.ID} value={outlet.ID}>{outlet.OutletName}</option>
                        ))}
                    </Form.Select>
                </div>

                <div className="field-stack">
                    <Form.Label>Member</Form.Label>
                    <Form.Select value={selectedMember} onChange={(e) => setSelectedMember(e.target.value)}>
                        <option value="">Select member</option>
                        {members.map(member => (
                            <option key={member.id} value={member.id}>{member.first_name} {member.last_name}</option>
                        ))}
                    </Form.Select>
                </div>

                <div className="field-stack">
                    <Form.Label>Modules</Form.Label>
                    <div className="check-grid">
                        {modules.map(module => (
                            <Form.Check
                                key={module.ID}
                                type="checkbox"
                                label={module.Modulename}
                                value={module.ID}
                                checked={selectedModules.has(module.ID)}
                                onChange={() => handleModuleChange(module.ID)}
                            />
                        ))}
                    </div>
                </div>

                <div className="form-actions">
                    <Button variant="primary" onClick={handleSave}>
                        {id ? 'Save Changes' : 'Save Assignment'}
                    </Button>
                    <Button variant="secondary" onClick={() => navigate('/member-modules')}>
                        Cancel
                    </Button>
                </div>
            </AdminFormLayout>
        </div>
    );
}

export default MemberModuleForm;
